import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const NAV_LINKS = [
  { label: 'How it works', to: '/#how-it-works' },
  { label: 'Why RideSaathi', to: '/#features' },
  { label: 'Rides', to: '/rides' },
  { label: 'Earnings', to: '/#earnings' },
  { label: 'Pricing', to: '/#pricing' },
  { label: 'FAQ', to: '/faq' },
];

const Navbar: React.FC = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navLinks = NAV_LINKS.map((link) => (
    link.to === '/rides' && token
      ? { ...link, to: '/app/rides' }
      : link
  ));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path: string) => {
    if (path.includes('#')) {
      return location.pathname === '/' && location.hash === path.slice(path.indexOf('#'));
    }
    return location.pathname === path;
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          bgcolor: '#061b31',
          color: 'white',
          backdropFilter: 'blur(18px)',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 2, md: 6 }, minHeight: { xs: 64, md: 72 } }}>
          {/* Brand */}
          <Box component={RouterLink} to="/" sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
            <Box component="img" src="/assets/ride-saathi-main-logo.png" alt="RideSaathi" sx={{ height: { xs: 38, md: 44 }, width: 'auto', display: 'block', filter: 'brightness(0) invert(1)' }} />
          </Box>

          {/* Desktop nav */}
          <Box display={{ xs: 'none', md: 'flex' }} gap={0.5} alignItems="center">
            {navLinks.map((link) => (
              <Button
                key={link.to}
                component={RouterLink}
                to={link.to}
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: isActive(link.to) ? '#ffc928' : 'rgba(255,255,255,0.84)',
                  borderRadius: 1,
                  px: 1.25,
                  minWidth: 0,
                  '&:hover': { color: '#ffc928', bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Box>

          {/* Desktop auth buttons */}
          <Box display={{ xs: 'none', md: 'flex' }} gap={1.5} alignItems="center">
            {token ? (
              <>
                <Button component={RouterLink} to="/app" variant="outlined" sx={{ borderColor: '#ffc928', color: 'white', fontWeight: 700 }}>
                  Dashboard
                </Button>
                <Button onClick={handleLogout} color="inherit" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button component={RouterLink} to="/login" variant="outlined" sx={{ fontWeight: 700, borderColor: '#ffc928', color: 'white' }}>
                  Login
                </Button>
                <Button
                  component={RouterLink}
                  to="/signup"
                  variant="contained"
                  sx={{ bgcolor: '#ffc928', color: '#07182d', fontWeight: 800, '&:hover': { bgcolor: '#f3b51b' } }}
                >
                  Sign up
                </Button>
              </>
            )}
          </Box>

          {/* Mobile hamburger */}
          <IconButton
            sx={{ display: { md: 'none' } }}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon sx={{ color: 'white' }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 300, bgcolor: '#061b31', color: 'white' } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box component={RouterLink} to="/" onClick={() => setDrawerOpen(false)} sx={{ textDecoration: 'none' }}>
            <Box component="img" src="/assets/ride-saathi-main-logo.png" alt="RideSaathi" sx={{ height: 36, width: 'auto', display: 'block', filter: 'brightness(0) invert(1)' }} />
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List sx={{ px: 1, pt: 1 }}>
          {navLinks.map((link) => (
            <ListItemButton
              key={link.to}
              component={RouterLink}
              to={link.to}
              onClick={() => setDrawerOpen(false)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive(link.to) ? 'rgba(255,201,40,0.14)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{ fontWeight: isActive(link.to) ? 700 : 500, color: isActive(link.to) ? '#ffc928' : 'rgba(255,255,255,0.85)' }}
              />
            </ListItemButton>
          ))}
        </List>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 2 }} />
        <Stack spacing={1.5} p={2} mt={1}>
          {token ? (
            <>
              <Button component={RouterLink} to="/app" variant="contained" fullWidth onClick={() => setDrawerOpen(false)} sx={{ bgcolor: '#ffc928', color: '#07182d', fontWeight: 800 }}>
                Dashboard
              </Button>
              <Button onClick={() => { handleLogout(); setDrawerOpen(false); }} variant="outlined" fullWidth sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}>
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button component={RouterLink} to="/signup" variant="contained" fullWidth onClick={() => setDrawerOpen(false)} sx={{ bgcolor: '#ffc928', color: '#07182d', fontWeight: 800 }}>
                Sign up
              </Button>
              <Button component={RouterLink} to="/login" variant="outlined" fullWidth onClick={() => setDrawerOpen(false)} sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}>
                Login
              </Button>
            </>
          )}
        </Stack>
      </Drawer>
    </>
  );
};

export default Navbar;
